import { PvaData, requestPvSystemDesign } from "@inrange/api-clients";
import { useSite, useSiteUpdate } from "@inrange/building-manager-api-client";
import {
  Building,
  getBuildingCoordinates,
  PvPanel,
  Site,
} from "@inrange/building-manager-api-client/models-site";
import area from "@turf/area";
import { polygon } from "@turf/helpers";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { UserContext } from "../../../auth/UserContext";
import { EditableBuilding } from "./BoundaryEditorMap";
import BulkReviewBoundaryAndRoofItem from "./BulkReviewBoundaryAndRoofItem";
import BulkReviewPanel from "./BulkReviewPanel";
import {
  getBoundaryReviewedAt,
  getPvLayoutQuality,
  PvLayoutQuality,
} from "./reviewState";

interface BulkReviewBoundaryAndRoofProps {
  title: string;
  sites: Site[];
  onClose: () => void;
}

const computeSurfaceAreaSqM = (b: EditableBuilding): number => {
  const coords = getBuildingCoordinates(b);
  try {
    return area(polygon(coords));
  } catch {
    return b.surfaceAreaSqM ?? 0;
  }
};

const buildingsChanged = (
  initial: Building[],
  current: Record<string, EditableBuilding>
): boolean => {
  const currentList = Object.values(current);
  if (currentList.length !== initial.length) return true;
  return currentList.some((b) => b.customised === true);
};

/**
 * Top-level wrapper for the boundary-and-roof bulk-review mode. Owns
 * the lifted state that has to survive row-switches (in-flight
 * recompute, the snapshot's locally-applied saves) and threads the
 * BMA hooks (useSite for pv panel options, useSiteUpdate for plain
 * field saves, requestPvSystemDesign for the post-edit recompute).
 *
 * Persistence model: every action is a plain update_site call (per
 * CLAUDE.md's "prefer plain field updates"). After a successful save
 * the wrapper applies the same field deltas to its local copy of
 * `sites` so the chips + per-site stage transition pick up the new
 * persisted state without a refetch.
 */
const BulkReviewBoundaryAndRoof: React.FC<BulkReviewBoundaryAndRoofProps> = ({
  title,
  sites: initialSites,
  onClose,
}) => {
  const { user } = useContext(UserContext);
  const userEmail = user?.email ?? "";

  // Localstorage-driven dev override that routes PVA to the e2e
  // fixture URL. Defaults to false everywhere - we always hit real
  // PVA via getPVAUrl(), which itself resolves to the per-branch /
  // staging / prod URL.
  const enableTestModePVARequests = Boolean(
    typeof window !== "undefined" &&
      window.localStorage.getItem("enableTestModePVARequests")
  );

  const { fetchSiteValues } = useSite({
    app: "admin",
    enableNonEssentialQueries: true,
  });
  const pvPanels = useMemo<Record<string, PvPanel>>(
    () =>
      (fetchSiteValues.data?.options?.pvPanels as Record<string, PvPanel>) ??
      {},
    [fetchSiteValues.data]
  );

  const [sites, setSites] = useState<Site[]>(() => sortSites(initialSites));
  // Sites whose boundary was edited in this session and whose PVA
  // recompute hasn't finished yet. Cleared from a useEffect below
  // when the matching pvQueries entry lands fresh panels (no task,
  // not fetching).
  const [editedSiteIds, setEditedSiteIds] = useState<Set<string>>(
    () => new Set()
  );
  // Sites whose most recent save failed. Surfaced as a "Save failed"
  // chip and cleared on the next successful save for the same site.
  const [errorSiteIds, setErrorSiteIds] = useState<Set<string>>(
    () => new Set()
  );

  // Mount a PVA query for every site in the snapshot on entry, so
  // designs start generating server-side in parallel while the
  // operator is still on the first row's boundary. Lazy-on-visit
  // gating made every site cost a full PVA roundtrip the first time
  // the operator landed on it.
  const pvQueries = useQueries({
    queries: sites.map((site) => {
      const panel = pvPanels[site.pvPanelName];
      return {
        queryKey: [
          "pv_system_design",
          site.id,
          panel,
          false,
          site.buildings,
        ] as const,
        queryFn: async () =>
          requestPvSystemDesign(
            { id: site.id, name: site.name, user: userEmail },
            panel!,
            site.buildings as Building[],
            {},
            undefined,
            enableTestModePVARequests
          ),
        enabled: !!panel,
        refetchOnWindowFocus: false,
        // PVA can return `task: {...}` to indicate the design is
        // still being generated. Poll every 30s while a task is
        // outstanding so the UI flips Loading -> ready without the
        // operator having to re-trigger the query (mirrors the
        // autoRefresh pattern in PVSystem.tsx:119-121).
        refetchInterval: (data: { data?: PvaData } | undefined) =>
          data?.data?.task ? 30_000 : false,
      };
    }),
  });

  const pvaBySiteId = useMemo(() => {
    const map: Record<
      string,
      {
        data: PvaData | undefined;
        isFetching: boolean;
        isError: boolean;
      }
    > = {};
    sites.forEach((s, i) => {
      const q = pvQueries[i];
      const r = q?.data as { data: PvaData } | undefined;
      map[s.id] = {
        data: r?.data,
        isFetching: q?.isFetching ?? false,
        isError: q?.isError ?? false,
      };
    });
    return map;
  }, [pvQueries, sites]);

  // PVA returns 200 instantly with a `task: {...}` descriptor when
  // the design isn't ready yet, so `isFetching` flips false before
  // panels actually land. Treat task-pending as still loading.
  const loadingPvSiteIds = useMemo(() => {
    const s = new Set<string>();
    sites.forEach((site, i) => {
      const q = pvQueries[i];
      const r = q?.data as { data?: PvaData } | undefined;
      if (q?.isFetching || r?.data?.task) s.add(site.id);
    });
    return s;
  }, [pvQueries, sites]);

  // Sites whose last PVA task terminally failed: the design is still
  // missing, nothing is running (PVA no longer auto-relaunches a failed
  // task for unchanged inputs - see pv-api get_task_status_or_launch_task),
  // and the task-results record reports task_run_success === false. This
  // is the state we surface as "PV failed" instead of looping on polling.
  const failedPvSiteIds = useMemo(() => {
    const s = new Set<string>();
    sites.forEach((site, i) => {
      const d = (pvQueries[i]?.data as { data?: PvaData } | undefined)?.data;
      if (
        d &&
        !d.task &&
        !d.geojson?.panels_aligned &&
        d.task_metrics?.task_run_success === false
      ) {
        s.add(site.id);
      }
    });
    return s;
  }, [pvQueries, sites]);

  // Clear sites from editedSiteIds once their PVA query lands fresh
  // panels (no task, not fetching). Until then the chip shows
  // "Recomputing PV…" so the operator doesn't think "PV pending" =
  // ready when the new design hasn't been generated yet.
  useEffect(() => {
    if (editedSiteIds.size === 0) return;
    const next = new Set(editedSiteIds);
    let changed = false;
    sites.forEach((site, i) => {
      if (!editedSiteIds.has(site.id)) return;
      const q = pvQueries[i];
      const r = q?.data as { data?: PvaData } | undefined;
      const panels = r?.data?.geojson?.panels_aligned;
      const task = r?.data?.task;
      // Clear the recompute flag when the new design lands (panels) OR when
      // the recompute terminally failed - otherwise "Recomputing PV…" would
      // stick forever on a failed re-run that PVA won't relaunch.
      const taskFailed = r?.data?.task_metrics?.task_run_success === false;
      if ((panels || taskFailed) && !task && !q?.isFetching) {
        next.delete(site.id);
        changed = true;
      }
    });
    if (changed) setEditedSiteIds(next);
  }, [pvQueries, sites, editedSiteIds]);

  const [filter, setFilter] = useState<FilterKey>("all");

  const updateSite = useSiteUpdate();

  const applyLocalSiteUpdate = useCallback(
    (siteId: string, patch: Partial<Site>) => {
      setSites((prev) =>
        prev.map((s) => (s.id === siteId ? { ...s, ...patch } : s))
      );
    },
    []
  );

  const rollbackSiteTo = useCallback((siteId: string, snapshot: Site) => {
    setSites((prev) => prev.map((s) => (s.id === siteId ? snapshot : s)));
  }, []);

  const clearSiteError = useCallback((siteId: string) => {
    setErrorSiteIds((prev) => {
      if (!prev.has(siteId)) return prev;
      const next = new Set(prev);
      next.delete(siteId);
      return next;
    });
  }, []);

  const flagSiteError = useCallback((siteId: string) => {
    setErrorSiteIds((prev) => {
      if (prev.has(siteId)) return prev;
      const next = new Set(prev);
      next.add(siteId);
      return next;
    });
  }, []);

  const handleAcceptBoundary = useCallback(
    async (
      site: Site,
      currentBuildings: Record<string, EditableBuilding>,
      helpers: { saveAndNext: () => void }
    ) => {
      const didEdit = buildingsChanged(site.buildings, currentBuildings);
      const now = Date.now();
      // Snapshot for rollback - if the save fails we restore the
      // local site to its pre-Accept state so the chip stops
      // claiming acceptance.
      const originalSite = site;

      // Recompute surface area for any drag-edited polygon (turf/area
      // mirrors BuildingMap.tsx) so totalBuildingArea reflects the
      // new geometry.
      const finalBuildings = Object.values(currentBuildings).map((b) => ({
        ...b,
        surfaceAreaSqM: b.customised
          ? computeSurfaceAreaSqM(b)
          : b.surfaceAreaSqM,
      }));
      const totalBuildingArea = finalBuildings.reduce(
        (sum, b) => sum + (b.surfaceAreaSqM ?? 0),
        0
      );

      // Local Site patch (optimistic). For an edit we bump boundary.at to
      // `now`, which supersedes any prior pvLayout review by timestamp (see
      // reviewState.ts) without having to delete it. For a no-edit accept we
      // keep the existing boundary.at if present, so a prior PV verdict stays
      // valid. reviews is a dict, so we build the full merged object locally
      // (applyLocalSiteUpdate is a shallow spread).
      const boundaryAt = didEdit ? now : (getBoundaryReviewedAt(site) ?? now);
      const newReviews = {
        ...site.reviews,
        boundary: { at: boundaryAt, by: userEmail },
      };
      const sitePatch: Partial<Site> = didEdit
        ? {
            buildings: finalBuildings as Building[],
            totalBuildingArea,
            reviews: newReviews,
          }
        : {
            reviews: newReviews,
          };

      // Wire payload. For edits, modified polygons MUST go through
      // the top-level `addedBuildings` collection-mutation key, with
      // BMA's expected snake_case-ish wire shape (key + surface_area_sqm).
      // Plain site.buildings updates are ignored by update_site
      // (which only looks at addedBuildings / deletedBuildings) - that
      // was the silent-failure bug Martin caught.
      const editedWireBuildings = didEdit
        ? Object.values(currentBuildings)
            .filter((b) => b.customised === true)
            .map((b) => ({
              key: b.id,
              surface_area_sqm: computeSurfaceAreaSqM(b),
              siteID: b.siteID,
              geometry: b.geometry,
              polygonType: "custom",
              sourcePolygonType: b.sourcePolygonType ?? "unknown",
              sourceGeometry: b.sourceGeometry,
              createdBy: b.createdBy || "",
              createdAt: b.createdAt || 0,
            }))
        : [];

      // Wire payload sends only the boundary sub-dict; BMA deep-merges it into
      // the existing reviews, preserving (now-stale-by-timestamp) pvLayout.
      const wirePayload = didEdit
        ? {
            site: {
              totalBuildingArea,
              reviews: { boundary: { at: boundaryAt, by: userEmail } },
            },
            addedBuildings: editedWireBuildings,
          }
        : {
            site: {
              reviews: { boundary: { at: boundaryAt, by: userEmail } },
            },
          };

      // Optimistic local update (same shape for edit vs no-edit; the no-edit
      // case stays on the same row because applyLocalSiteUpdate sets
      // reviews.boundary.at -> stage flips to "pv").
      applyLocalSiteUpdate(site.id, sitePatch);
      clearSiteError(site.id);

      if (didEdit) {
        setEditedSiteIds((prev) => {
          const next = new Set(prev);
          next.add(site.id);
          return next;
        });
        helpers.saveAndNext();
      }

      try {
        await updateSite.mutateAsync({
          siteId: site.id,
          payload: wirePayload,
        });
      } catch (e) {
        // Roll back the optimistic update so the chip doesn't keep
        // claiming the save landed. Also clear the editedSiteIds
        // flag so the "Recomputing PV…" chip doesn't get stuck on a
        // recompute that's not happening.
        rollbackSiteTo(site.id, originalSite);
        if (didEdit) {
          setEditedSiteIds((prev) => {
            if (!prev.has(site.id)) return prev;
            const next = new Set(prev);
            next.delete(site.id);
            return next;
          });
        }
        flagSiteError(site.id);
        console.error("bulk-review boundary save failed", e);
      }
    },
    [
      applyLocalSiteUpdate,
      clearSiteError,
      flagSiteError,
      rollbackSiteTo,
      updateSite,
      userEmail,
    ]
  );

  const handleMarkPvQuality = useCallback(
    async (
      site: Site,
      quality: PvLayoutQuality,
      helpers: { saveAndNext: () => void }
    ) => {
      const now = Date.now();
      const originalSite = site;
      const pvLayout = { at: now, by: userEmail, quality };
      const patch: Partial<Site> = {
        reviews: { ...site.reviews, pvLayout },
      };
      // Optimistic + advance, then settle. Rolls back on failure.
      applyLocalSiteUpdate(site.id, patch);
      clearSiteError(site.id);
      helpers.saveAndNext();
      try {
        await updateSite.mutateAsync({
          siteId: site.id,
          payload: {
            site: {
              reviews: { pvLayout },
            },
          },
        });
      } catch (e) {
        rollbackSiteTo(site.id, originalSite);
        flagSiteError(site.id);
        console.error("bulk-review PV quality save failed", e);
      }
    },
    [
      applyLocalSiteUpdate,
      clearSiteError,
      flagSiteError,
      rollbackSiteTo,
      updateSite,
      userEmail,
    ]
  );

  const filterCounts = useMemo(() => {
    const counts: Record<FilterKey, number> = {
      all: sites.length,
      boundaryPending: 0,
      pvPending: 0,
      high: 0,
      medium: 0,
      low: 0,
    };
    for (const s of sites) {
      const quality = getPvLayoutQuality(s);
      if (!getBoundaryReviewedAt(s)) counts.boundaryPending += 1;
      else if (!quality && !editedSiteIds.has(s.id)) counts.pvPending += 1;
      if (quality === "high") counts.high += 1;
      if (quality === "medium") counts.medium += 1;
      if (quality === "low") counts.low += 1;
    }
    return counts;
  }, [sites, editedSiteIds]);

  const filteredSites = useMemo(() => {
    return sites.filter((s) => siteMatchesFilter(s, filter, editedSiteIds));
  }, [sites, filter, editedSiteIds]);

  const renderListHeader = useCallback(
    function renderListHeader() {
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {FILTER_OPTIONS.map(({ key, label }) => (
            <FilterChip
              key={key}
              active={filter === key}
              onClick={() => setFilter(key)}
              label={label}
              count={filterCounts[key]}
            />
          ))}
        </div>
      );
    },
    [filter, filterCounts]
  );

  const renderRowSecondary = useCallback(
    function renderRowSecondary(site: Site) {
      const accepted = !!getBoundaryReviewedAt(site);
      const recomputing = editedSiteIds.has(site.id);
      const quality = getPvLayoutQuality(site);
      const loadingPv = loadingPvSiteIds.has(site.id);
      const pvFailed = failedPvSiteIds.has(site.id);
      const saveFailed = errorSiteIds.has(site.id);
      return (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {accepted ? (
            <Chip kind="boundary-accepted">Boundary ✓</Chip>
          ) : (
            <Chip kind="boundary-pending">Boundary pending</Chip>
          )}
          {recomputing ? (
            <Chip kind="recomputing">Recomputing PV…</Chip>
          ) : accepted && !quality && pvFailed ? (
            <Chip kind="pv-failed">PV failed</Chip>
          ) : accepted && !quality && loadingPv ? (
            <Chip kind="pv-loading">Loading PV…</Chip>
          ) : accepted && !quality ? (
            <Chip kind="pv-pending">PV pending</Chip>
          ) : null}
          {quality ? <Chip kind={`pv-${quality}`}>PV {quality}</Chip> : null}
          {saveFailed ? (
            <Chip kind="save-failed">Save failed</Chip>
          ) : null}
        </div>
      );
    },
    [editedSiteIds, loadingPvSiteIds, failedPvSiteIds, errorSiteIds]
  );

  return (
    <BulkReviewPanel
      title={title}
      sites={filteredSites}
      onClose={onClose}
      renderRowSecondary={renderRowSecondary}
      renderListHeader={renderListHeader}
      renderItem={(helpers) => (
        <BulkReviewBoundaryAndRoofItem
          key={helpers.site.id}
          helpers={helpers}
          isRecomputingPv={editedSiteIds.has(helpers.site.id)}
          isSaving={false}
          pvaData={pvaBySiteId[helpers.site.id]?.data}
          pvaIsFetching={pvaBySiteId[helpers.site.id]?.isFetching ?? false}
          pvaIsError={pvaBySiteId[helpers.site.id]?.isError ?? false}
          onAcceptBoundary={(currentBuildings) =>
            handleAcceptBoundary(helpers.site, currentBuildings, helpers)
          }
          onMarkPvQuality={(quality) =>
            handleMarkPvQuality(helpers.site, quality, helpers)
          }
        />
      )}
    />
  );
};

type FilterKey =
  | "all"
  | "boundaryPending"
  | "pvPending"
  | "high"
  | "medium"
  | "low";

// Stable alphabetical sort (case-insensitive on name, id as
// tiebreak). Applied once when the wrapper mounts so the list order
// doesn't shuffle on refresh / re-snapshot.
const sortSites = (xs: Site[]): Site[] =>
  [...xs].sort((a, b) => {
    const an = (a.name || "").toLocaleLowerCase();
    const bn = (b.name || "").toLocaleLowerCase();
    if (an < bn) return -1;
    if (an > bn) return 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "boundaryPending", label: "Boundary pending" },
  { key: "pvPending", label: "PV pending" },
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
];

const siteMatchesFilter = (
  site: Site,
  filter: FilterKey,
  editedSiteIds: Set<string>
): boolean => {
  switch (filter) {
    case "all":
      return true;
    case "boundaryPending":
      return !getBoundaryReviewedAt(site);
    case "pvPending":
      return (
        !!getBoundaryReviewedAt(site) &&
        !getPvLayoutQuality(site) &&
        !editedSiteIds.has(site.id)
      );
    case "high":
    case "medium":
    case "low":
      return getPvLayoutQuality(site) === filter;
    default:
      return true;
  }
};

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`bulk-review-filter-${label}`}
      style={{
        border: active ? "1px solid #0d6efd" : "1px solid #ced4da",
        backgroundColor: active ? "#e7f1ff" : "white",
        color: active ? "#0d6efd" : "#495057",
        borderRadius: 12,
        padding: "2px 8px",
        fontSize: 11,
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {label} {count}
    </button>
  );
}

const chipStyles: Record<string, React.CSSProperties> = {
  "boundary-pending": { backgroundColor: "#f1f3f5", color: "#495057" },
  "boundary-accepted": { backgroundColor: "#d4edda", color: "#155724" },
  "pv-pending": { backgroundColor: "#fff3cd", color: "#856404" },
  "pv-loading": { backgroundColor: "#e2e3e5", color: "#41464b" },
  recomputing: { backgroundColor: "#cfe2ff", color: "#084298" },
  "pv-high": { backgroundColor: "#198754", color: "white" },
  "pv-medium": { backgroundColor: "#fd7e14", color: "white" },
  "pv-low": { backgroundColor: "#dc3545", color: "white" },
  "pv-failed": { backgroundColor: "#f8d7da", color: "#842029" },
  "save-failed": { backgroundColor: "#f8d7da", color: "#842029" },
};

function Chip({
  kind,
  children,
}: {
  kind: string;
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        ...chipStyles[kind],
        fontSize: 11,
        padding: "2px 6px",
        borderRadius: 10,
        whiteSpace: "nowrap",
      }}
      data-testid={`bulk-review-chip-${kind}`}
    >
      {children}
    </span>
  );
}

export default BulkReviewBoundaryAndRoof;
